import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useAuth } from 'react-oidc-context';
import { Block } from '../Block';
import './index.scss';

const DEFAULT_FORUM_URL = 'https://forum.emdrassociation.org.uk';
const MAX_VISIBLE_TOPICS = 2;

export interface ForumTopic {
    id: string;
    title: string;
    permalink: string;
    authorName?: string;
    forumTitle?: string;
    postedAt?: string;
    replyCount?: number;
    summary?: string;
}

interface BbpApiTopic {
    id?: number | string;
    title?: string | { rendered?: string };
    reply_count?: number | string;
    permalink?: string;
    link?: string;
    author_name?: string;
    post_date?: string;
    date?: string;
    forum_title?: string;
}

interface BbpApiResponse {
    topics?: BbpApiTopic[];
}

const stripTrailingSlash = (value: string) => value.replace(/\/$/, '');

const getForumUrl = () => process.env.REACT_APP_FORUM_URL || DEFAULT_FORUM_URL;

const getMemberApiForumFeedEndpoint = () => {
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    if (!apiEndpoint) return undefined;

    return `${stripTrailingSlash(apiEndpoint)}/Forum/Feed`;
};

const getForumEndpoint = () =>
    process.env.REACT_APP_FORUM_API_URL ||
    process.env.REACT_APP_FORUM_FEED_URL ||
    getMemberApiForumFeedEndpoint() ||
    `${stripTrailingSlash(getForumUrl())}/all-forums/feed`;

const shouldSendBearerToken = () => process.env.REACT_APP_FORUM_AUTH_MODE === 'bearer';

const isMemberApiEndpoint = (endpoint: string) => {
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    return Boolean(apiEndpoint && endpoint.startsWith(stripTrailingSlash(apiEndpoint)));
};

const shouldAttachAccessToken = (endpoint: string, accessToken?: string) =>
    Boolean(accessToken && (isMemberApiEndpoint(endpoint) || shouldSendBearerToken()));

const getCredentialsMode = (endpoint: string): RequestCredentials =>
    isMemberApiEndpoint(endpoint) || shouldSendBearerToken() ? 'omit' : 'include';

const getAuthModeLabel = (endpoint: string) =>
    isMemberApiEndpoint(endpoint)
        ? 'coalesce-api-bearer'
        : shouldSendBearerToken()
          ? 'bearer'
          : 'browser-credentials';

const getErrorMessage = (error: unknown) =>
    error instanceof Error ? error.message : 'Unknown forum feed error.';

export const decodeHtmlEntities = (value?: string) => {
    if (!value) return '';

    const doc = new DOMParser().parseFromString(value, 'text/html');
    return doc.documentElement.textContent ?? value;
};

const getPlainTextFromHtml = (value?: string) => {
    if (!value) return '';

    const doc = new DOMParser().parseFromString(value, 'text/html');
    return (doc.body.textContent ?? doc.documentElement.textContent ?? value)
        .replace(/\s+/g, ' ')
        .trim();
};

const truncate = (value: string, maxLength = 115) =>
    value.length > maxLength ? `${value.slice(0, maxLength).trim()}...` : value;

const getChildText = (element: Element, childNames: string[]) => {
    const names = childNames.map((name) => name.toLowerCase());
    const child = Array.from(element.children).find((candidate) =>
        names.includes(candidate.localName.toLowerCase())
    );

    return child?.textContent?.trim() ?? '';
};

const getSlashComments = (element: Element) => {
    const child = Array.from(element.children).find(
        (candidate) =>
            candidate.localName.toLowerCase() === 'comments' &&
            candidate.namespaceURI?.includes('slash')
    );
    const parsed = Number(child?.textContent);

    return Number.isNaN(parsed) ? undefined : parsed;
};

export const parseRssTopics = (xml: string): ForumTopic[] => {
    const doc = new DOMParser().parseFromString(xml, 'text/xml');

    if (doc.querySelector('parsererror')) {
        throw new Error('Unable to read forum feed.');
    }

    return Array.from(doc.querySelectorAll('item')).map((item, index) => {
        const permalink = getChildText(item, ['link']);
        const guid = getChildText(item, ['guid']);

        return {
            id: guid || permalink || `rss-${index}`,
            title: decodeHtmlEntities(getChildText(item, ['title'])),
            permalink,
            authorName: decodeHtmlEntities(getChildText(item, ['creator', 'author'])),
            forumTitle: decodeHtmlEntities(getChildText(item, ['category'])) || 'Forum',
            postedAt: getChildText(item, ['pubDate']),
            replyCount: getSlashComments(item),
            summary: truncate(getPlainTextFromHtml(getChildText(item, ['description']))),
        };
    });
};

const getApiTopicTitle = (topic: BbpApiTopic) => {
    if (typeof topic.title === 'string') return topic.title;
    return topic.title?.rendered ?? '';
};

export const parseApiTopics = (response: BbpApiResponse | BbpApiTopic[]): ForumTopic[] => {
    const topics = Array.isArray(response) ? response : (response.topics ?? []);

    return topics.map((topic, index) => ({
        id: String(topic.id ?? topic.permalink ?? topic.link ?? `api-${index}`),
        title: decodeHtmlEntities(getApiTopicTitle(topic)),
        permalink: topic.permalink ?? topic.link ?? getForumUrl(),
        authorName: decodeHtmlEntities(topic.author_name),
        forumTitle: decodeHtmlEntities(topic.forum_title) || 'Forum',
        postedAt: topic.post_date ?? topic.date,
        replyCount:
            topic.reply_count === undefined || topic.reply_count === null
                ? undefined
                : Number(topic.reply_count),
        summary: undefined,
    }));
};

export const fetchForumTopics = async (endpoint: string, accessToken?: string) => {
    const headers = new Headers({
        Accept: 'application/rss+xml, application/xml, text/xml, application/json',
    });
    const sendBearerToken = shouldAttachAccessToken(endpoint, accessToken);
    const credentials = getCredentialsMode(endpoint);

    if (sendBearerToken && accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
    }

    console.info('[ForumBlock] Fetching forum posts', {
        endpoint,
        credentials,
        authMode: getAuthModeLabel(endpoint),
        bearerTokenAttached: sendBearerToken,
    });

    const response = await fetch(endpoint, {
        credentials,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';

    console.info('[ForumBlock] Forum feed response', {
        endpoint,
        status: response.status,
        ok: response.ok,
        contentType,
    });

    if (!response.ok) {
        const errorBody = await response.text().catch(() => '');
        throw new Error(
            `Unable to load forum posts (${response.status}). ${errorBody.slice(0, 250)}`
        );
    }

    const body = await response.text();
    const trimmedBody = body.trim();

    if (
        contentType.includes('json') ||
        trimmedBody.startsWith('{') ||
        trimmedBody.startsWith('[')
    ) {
        return parseApiTopics(JSON.parse(trimmedBody));
    }

    return parseRssTopics(body);
};

const formatPostDate = (value?: string) => {
    if (!value) return 'Recently';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
    }).format(date);
};

const getPostCountLabel = (count?: number) => {
    if (count === undefined) return undefined;

    return `${count} ${count === 1 ? 'post' : 'posts'}`;
};

const getNewPostsLabel = (count?: number) => {
    if (count === undefined) return 'New post';

    return `${count} new ${count === 1 ? 'post' : 'posts'}`;
};

const LatestTopicsWidget: React.FC = () => {
    const auth = useAuth();
    const forumUrl = getForumUrl();
    const endpoint = useMemo(() => getForumEndpoint(), []);
    const [topics, setTopics] = useState<ForumTopic[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();

    useEffect(() => {
        const loadTopics = async () => {
            setLoading(true);

            try {
                const result = await fetchForumTopics(endpoint, auth.user?.access_token);
                console.info('[ForumBlock] Forum posts loaded', {
                    endpoint,
                    count: result.length,
                    source: 'live-feed',
                });
                setTopics(result);
                setError(
                    result.length === 0 ? 'The forum feed did not return any posts yet.' : undefined
                );
            } catch (err) {
                console.error('[ForumBlock] Failed to load forum posts', {
                    forumUrl,
                    endpoint,
                    authMode: getAuthModeLabel(endpoint),
                    credentials: getCredentialsMode(endpoint),
                    message: getErrorMessage(err),
                    error: err,
                });
                setTopics([]);
                setError(getErrorMessage(err));
            } finally {
                setLoading(false);
            }
        };

        loadTopics();
    }, [auth.user?.access_token, endpoint]);

    return (
        <Block title="Forum (Latest)">
            {loading && <div>Loading forum posts...</div>}

            {!loading && error && (
                <Alert variant="danger" className="py-2 small">
                    Unable to load forum posts: {error}
                </Alert>
            )}

            {!loading && !error && topics.length === 0 && <div>No forum posts found yet.</div>}

            {!loading && topics.length > 0 && (
                <ul className="forum-block-list">
                    {topics.slice(0, MAX_VISIBLE_TOPICS).map((topic) => (
                        <li key={topic.id} className="forum-block-card">
                            <div className="forum-block-badge">
                                {getNewPostsLabel(topic.replyCount)}
                            </div>

                            <a
                                href={topic.permalink || forumUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="forum-block-link"
                            >
                                <h5 className="forum-block-post-title">{topic.title}</h5>
                            </a>

                            {topic.summary && (
                                <p className="forum-block-summary">{topic.summary}</p>
                            )}

                            <p className="forum-block-meta">
                                {getPostCountLabel(topic.replyCount) && (
                                    <>
                                        {getPostCountLabel(topic.replyCount)}
                                        <span className="forum-block-meta-dot">&middot;</span>
                                    </>
                                )}
                                Last post {formatPostDate(topic.postedAt)}
                                {topic.authorName && <> by {topic.authorName}</>}
                            </p>
                        </li>
                    ))}
                </ul>
            )}

            <Button
                className="forum-block-cta"
                variant="secondary"
                href={forumUrl}
                target="_blank"
                rel="noreferrer"
            >
                Add your voice
            </Button>
        </Block>
    );
};

export default LatestTopicsWidget;
